import React from 'react';

import { AlertTitle, Divider, styled } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useContainer } from 'unstated-next';

import _ from 'lodash';
import { FieldError } from 'react-hook-form';
import { StyledLoadingButton as LoadingButton } from '../../components/forms/LoadingButton';
import ClassContainer from './ClassContainer';
import { ClassFormContainer, StyledButton, StyledFooter, StyledForm, StyledTypography } from './ClassForm.styles';
import ErrorForm from './ErrorForm';
import { LoadingForm } from './LoadingForm';
import Advanced from './Sections/Advanced';
import Dates from './Sections/Dates/index';
import Details from './Sections/Details';

import { Alert } from '../../components/Alert';

type ValidationError = {
    message: string;
    type: string;
    ref: { [key: string]: any };
};

type DateErrors = {
    days: ValidationError;
    dateRange: ValidationError;
};

type ErrorObject = {
    [key: string]: FieldError | DateErrors[] | ErrorObject | any;
};

const extractErrorMessages = (errorsObj: ErrorObject) => {
    const errorMessages: string[] = [];

    _.forEach(errorsObj, (error: any) => {
        if (error?.message) {
            errorMessages.push(error.message);
        } else {
            errorMessages.push(...extractErrorMessages(error));
        }
    });

    return errorMessages;
};

const StyledLoadingButton = styled(LoadingButton)`
    margin-left: 0.75rem;
`;

const StyledDivider = styled(Divider)`
    margin: 0 1.5rem;
`;

const ClassForm: React.FC = () => {
    const { t } = useTranslation('create_edit_class');
    const { t: tCommon } = useTranslation('common');

    const {
        handleSubmit,
        createClass,
        updateClass,
        onCancel,
        errorMessage,
        createClassStatus,
        updateClassStatus,
        formActionType,
        getClassToEditStatus,
        formErrors
    } = useContainer(ClassContainer);

    const extractedFormErrors = extractErrorMessages(formErrors);

    const onSubmit = async () => {
        if (formActionType === 'CREATE') {
            await createClass();
        }

        if (formActionType === 'EDIT') {
            await updateClass();
        }
    };

    const returnTitle = () => {
        if (formActionType === 'CREATE') {
            return t('create-title');
        }

        if (formActionType === 'EDIT') {
            return t('edit-title');
        }
    };

    // @NOTE: we can get more granular with the loading for a better UI, giving smaller components their own styled skeleton to display if loading
    const isLoadingEditClassForm = formActionType === 'EDIT' && getClassToEditStatus !== 'SUCCESS';
    const errorLoadingEditClassForm = formActionType === 'EDIT' && getClassToEditStatus === 'ERROR';

    const returnSubmitButton = () => {
        if (formActionType === 'CREATE') {
            return (
                <StyledLoadingButton
                    disabled={createClassStatus === 'LOADING'}
                    variant="contained"
                    type="submit"
                    loading={createClassStatus === 'LOADING'}
                >
                    {t('schedule')}
                </StyledLoadingButton>
            );
        }

        if (formActionType === 'EDIT') {
            return (
                <StyledLoadingButton
                    disabled={updateClassStatus === 'LOADING'}
                    variant="contained"
                    type="submit"
                    loading={updateClassStatus === 'LOADING'}
                >
                    {t('save')}
                </StyledLoadingButton>
            );
        }
    };

    const isAsyncError = createClassStatus === 'ERROR' || updateClassStatus === 'ERROR';

    const returnForm = () => {
        if (errorLoadingEditClassForm) {
            return <ErrorForm errorMessage={errorMessage} />;
        }

        if (isLoadingEditClassForm) {
            return <LoadingForm />;
        }

        return (
            <StyledForm onSubmit={handleSubmit(onSubmit)}>
                <Details />
                <StyledDivider />
                <Dates />
                <StyledDivider />
                <Advanced parentPage='scheduler' />

                {(isAsyncError) && (
                    <Alert severity='error'>
                        <AlertTitle>{tCommon('somethingWentWrong')}</AlertTitle>
                        {errorMessage}
                    </Alert>
                )}

                {extractedFormErrors.length ? (
                    <Alert severity='error'>
                        <AlertTitle>{tCommon('addressTheIssues')}</AlertTitle>
                        <ul style={{ paddingInlineStart: 0 }}>
                            {extractedFormErrors.map((error: string, i: number) => (
                                <li key={i}>{error}</li>
                            ))}
                        </ul>
                    </Alert>
                ) : null}

                <StyledFooter>
                    <StyledButton onClick={() => onCancel()} variant="outlined">{t('cancel')}</StyledButton>
                    {returnSubmitButton()}
                </StyledFooter>
            </StyledForm>
        );
    };

    return (
        <ClassFormContainer>
            <StyledTypography variant='h5'>{returnTitle()}</StyledTypography>
            {returnForm()}
        </ClassFormContainer>
    );
};

export default ClassForm;
